import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Fab,
  Drawer,
  Chip,
  CircularProgress,
  useTheme,
  Alert,
  Button,
} from '@mui/material';
import {
  Send,
  Mic,
  Close,
  SmartToy,
  TrendingUp,
  AddCircle,
  Receipt,
  Inventory,
  Settings,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContextMongo';
import { usePermissions } from '../../context/PermissionsContext';
import { getAssistantResponse } from '../../services/aiService';

const quickReplies = [
  "¿Cómo registro una venta?",
  "¿Cómo consulto el inventario?",
  "¿Qué productos tienen bajo stock?"
];

const AIAssistant = () => {
  const theme = useTheme();
  const location = useLocation();
  const { user } = useAuth();
  const { userRole } = usePermissions();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: '¡Hola! Soy tu asistente de POS. ¿En qué puedo ayudarte hoy? 😊',
    },
  ]);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const chatEndRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!userInput.trim() || isLoading) return;

    const newMessages = [...messages, { role: 'user', content: userInput }];
    setMessages(newMessages);
    setUserInput('');
    setError(null);
    setIsLoading(true);

    try {
      // Usar solo la función local para responder
      const response = await getAssistantResponse(userInput, location.pathname, userRole, user?.uid, messages);
      setMessages((prev) => [...prev, { role: 'assistant', content: response.text }]);
    } catch (err) {
      setError(err.message);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Ocurrió un error al generar la respuesta local. Por favor, intenta nuevamente.' },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <Fab
        color="primary"
        aria-label="assistant"
        onClick={() => setIsOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1000,
        }}
      >
        <SmartToy />
      </Fab>

      <Drawer
        anchor="right"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        PaperProps={{
          sx: {
            width: '350px',
            maxWidth: '100%',
            borderRadius: '16px 0 0 16px',
          }
        }}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          bgcolor: theme.palette.background.default
        }}>
          {/* Header */}
          <Box sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmartToy color="primary" />
              <Typography variant="h6">Asistente IA</Typography>
            </Box>
            <IconButton onClick={() => setIsOpen(false)}>
              <Close />
            </IconButton>
          </Box>

          {/* Sugerencias rápidas */}
          <Box sx={{ display: 'flex', gap: 1, p: 2, pt: 1 }}>
            {quickReplies.map((text, idx) => (
              <Button
                key={idx}
                variant="outlined"
                size="small"
                onClick={() => setUserInput(text)}
                disabled={isLoading}
              >
                {text}
              </Button>
            ))}
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => setMessages([{ role: 'assistant', content: '¡Hola! Soy tu asistente de POS. ¿En qué puedo ayudarte hoy? 😊' }])}
              disabled={isLoading}
            >
              Borrar chat
            </Button>
          </Box>

          {/* Chat Area */}
          <Box sx={{
            flexGrow: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {messages.map((msg, index) => (
              <Box
                key={index}
                sx={{
                  alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '90%',
                  mb: 1
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    bgcolor: msg.role === 'user' ? 'primary.main' : 'background.paper',
                    color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                    borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    border: msg.role === 'user' ? 'none' : `1px solid ${theme.palette.divider}`,
                    boxShadow: msg.role === 'user' ? '0 4px 12px rgba(25, 118, 210, 0.2)' : '0 2px 8px rgba(0,0,0,0.05)',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {msg.content}
                  </Typography>
                </Paper>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', px: 1 }}>
                  {msg.role === 'user' ? 'Tú' : 'Asistente'}
                </Typography>
              </Box>
            ))}
            {isLoading && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">Pensando...</Typography>
              </Box>
            )}
            <div ref={chatEndRef} />
          </Box>

          {/* Input Area */}
          <Box sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Escribe tu pregunta..."
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={handleKeyPress}
                size="small"
                error={!!error}
                multiline
                maxRows={4}
              />
              <IconButton
                color="primary"
                onClick={handleSend}
                disabled={!userInput.trim() || isLoading}
              >
                <Send />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default AIAssistant;
